// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import Image from 'presentations/views/components/atoms/Image';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Button from 'presentations/views/components/atoms/Button';
import ListLinkButton, {
  EventLink,
} from 'presentations/views/components/molecules/ListLinkButton';
import Avatar, { Group } from 'presentations/views/components/atoms/Avatar';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  black,
  white,
  gray01,
  gray02,
  gray04,
} from 'presentations/views/styles/color';
import { Role } from 'domains/models/team_user/role';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { TaxRateListItem } from 'presentations/api/schema/tax_rate';

export interface Props {
  teamUid: string;
  taxRate: TaxRateListItem;
  role: Role;
  onClickMenu?: any;
  isLoading?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const taxRate = props.taxRate;

  const renderContent = useMemo(
    () => (
      <>
        <EventTitle>{t(`taxType_${taxRate.taxType}`)}</EventTitle>
        <EventTitle>
          {taxRate.percentage}%{t(`taxInclusive_${taxRate.taxInclusive}`)}
        </EventTitle>
      </>
    ),
    [taxRate],
  );
  return (
    <Card data-intercom-target="tax-rate-list-item">
      <Content>
        <Header>
          <Block>
            <ShownPCSite>{renderContent}</ShownPCSite>
          </Block>
        </Header>
        <ShownSPSite>{renderContent}</ShownSPSite>
      </Content>
      <MenuContainer data-intercom-target="tax-rate-list-item-menu">
        {props.role <= Role.Admin && (
          <IconButton
            onClick={(e: SyntheticEvent) => props.onClickMenu(e, taxRate)}
          >
            <SettingIcon name="settings" />
          </IconButton>
        )}
      </MenuContainer>
    </Card>
  );
}

export function LoadingComponent() {
  return (
    <Card>
      <Content>
        <Header>
          <Block>
            <TagList>
              <Skeleton width="100px" variant="text" />
            </TagList>
            <EventTitle>
              <Skeleton width="120px" variant="text" />
            </EventTitle>
          </Block>
        </Header>
        <ShownSPSite></ShownSPSite>
      </Content>
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  position: relative;
  background: ${white};
  padding: ${spacing(32)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  ${mediaMax.desktop} {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: ${spacing(24)};
  }
`;
const Content = styled.div`
  width: 100%;
`;
const Header = styled.div`
  display: flex;
  width: 100%;
  color: ${gray01};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const TagList = styled.div`
  margin-bottom: ${spacing(8)};
  display: flex;
  ${mediaMax.tablet} {
    margin-bottom: 0;
  }
`;
const Block = styled.div`
  width: 100%;
  ${mediaMax.tablet} {
    margin-right: ${spacing(24)};
  }
`;
const EventTitle = styled.div`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  :first-of-type {
    margin-bottom: ${spacing(20)};
  }
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;

const MenuContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const ShownPCSite = styled.div`
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ShownSPSite = styled.div`
  display: none;
  ${mediaMax.tablet} {
    display: block;
  }
`;

const SettingIcon = styled(Icon)`
  color: ${gray01};
`;
